'use strict'
const _ = require('lodash')
const {REF_DELIMITER} = require('../../utils/constants')

const removeHash = value => value && value.replace('#', '')
const getOverridesRefArrayId = refArrayId => `${refArrayId}-refArrayOverride`

// Receives resolved ref array and return data overrides to merge.
const getDataOverrides = (resolvedRefArray, dataQuery) => {
    const dataOverrides = {}
    dataOverrides[dataQuery] = {
        ...resolvedRefArray,
        id: dataQuery,
        values: resolvedRefArray.values.map(value => `#${value.id}`)
    }

    resolvedRefArray.values.forEach(value => {
        if (value.type === 'VariantRelation') {
            dataOverrides[value.id] = {...value, to: `#${value.to.id}`}
            dataOverrides[value.to.id] = value.to
        } else {
            dataOverrides[value.id] = value
        }
    })
    return dataOverrides
}

// Returns the item under the preset condition.
// If there is no item under the preset condition, returns the first item which isn't not under any condition.
const getItemUnderPresetCondition = (presetCondition, refArray) => {
    const getDefaultItem = () => refArray.values.find(value => value.type !== 'VariantRelation') // there should always be a default value.

    const variantRelationWithPresetCondition = refArray.values
        .find(value => value.type === 'VariantRelation' && value.variants
            .some(variantId => removeHash(variantId) === presetCondition))
    return variantRelationWithPresetCondition ? variantRelationWithPresetCondition.to : getDefaultItem()
}

// "ref1_r_ref2_compId-query => ["ref2_compId-query", "ref1_r_ref2_compId-query"]
const getQueries = dataQuery => {
    const res = []
    _(dataQuery).split(REF_DELIMITER).reverse().forEach((refId, index) => {
        if (index > 0) {
            res.push(`${refId}${REF_DELIMITER}${res[index - 1]}`)
        } else {
            res.push(refId)
        }
    })
    res.shift()
    return res
}

const inflateAndResolveRefArrayValues = (refArrayValues, dataMap, dataQuery) => refArrayValues.map(itemId => {
    const resolvedItem = dataMap[removeHash(itemId)]
    if (resolvedItem.type !== 'VariantRelation') {
        const inflatedItemId = `${dataQuery}-${resolvedItem.id}`
        return {
            ...resolvedItem,
            id: inflatedItemId
        }
    }
    const to = dataMap[removeHash(resolvedItem.to)]
    const inflatedVariantRelationId = `${dataQuery}-${resolvedItem.id}`
    const inflatedItemId = `${dataQuery}-${to.id}`
    return {
        ...resolvedItem,
        to: {
            ...to,
            id: inflatedItemId
        },
        id: inflatedVariantRelationId
    }
})

const inflateResolvedRefArray = (resolvedRefArray, dataQuery) => ({
    ...resolvedRefArray,
    id: dataQuery,
    values: resolvedRefArray.values.map(value => {
        if (value.type === 'VariantRelation') {
            const to = value.to
            const inflatedVariantRelationId = `${dataQuery}-${value.id}`
            const inflatedItemId = `${dataQuery}-${to.id}`
            return {
                ...value,
                to: {
                    ...to,
                    id: inflatedItemId
                },
                id: inflatedVariantRelationId
            }
        }
        const inflatedItemId = `${dataQuery}-${value.id}`
        return {
            ...value,
            id: inflatedItemId
        }
    })
})

const resolveItemsUnderPreset = (presetDataItem, presetItemKey, resolvedRefArray, dataQuery, dataMap) => {
    if (presetDataItem.type === 'PresetData') {
        const item = getItemUnderPresetCondition(presetDataItem[presetItemKey], resolvedRefArray)
        const inflatedItemId = `${dataQuery}-${presetDataItem.id}-${presetItemKey}`
        return dataMap[inflatedItemId] ? dataMap[inflatedItemId] : {
            ...item,
            id: inflatedItemId
        }
    }

    const presetItem = presetDataItem.to
    const inflatedVariantRelationId = `${dataQuery}-${presetDataItem.id}`
    const item = getItemUnderPresetCondition(presetItem[presetItemKey], resolvedRefArray)
    const inflatedItemId = `${dataQuery}-${presetDataItem.id}-${presetItemKey}`
    if (dataMap[inflatedVariantRelationId]) {
        // We resolve the "to" in order to be consistent with the flow when there is no override
        return {
            ...dataMap[inflatedVariantRelationId],
            to: dataMap[removeHash(dataMap[inflatedVariantRelationId].to)]
        }
    }
    return {
        ...presetDataItem,
        id: inflatedVariantRelationId,
        to: dataMap[inflatedItemId] ? dataMap[inflatedItemId] : {
            ...item,
            id: inflatedItemId
        }
    }
}

function inflateWithoutPresets(resolvedRefArray, dataQuery, dataMap) {
    // TODO: Fix this logic to work for multiple level overrides.
    const inflatedResolvedRefArray = inflateResolvedRefArray(resolvedRefArray, dataQuery)

    const overridesRefArray = dataMap[getOverridesRefArrayId(dataQuery)]
    if (overridesRefArray) {
        const inflatedOverridesRefArrayValues = inflateAndResolveRefArrayValues(overridesRefArray.values, dataMap, dataQuery)
        inflatedResolvedRefArray.values = inflatedResolvedRefArray.values.concat(inflatedOverridesRefArrayValues)
    }

    return getDataOverrides(inflatedResolvedRefArray, dataQuery)
}

const inflateWithPresets = (resolvedRefArray, dataQuery, presets, mapName, dataMap) => {
    const presetItemKey = mapName === 'layout_data' ? 'layout' : 'style'
    const queries = getQueries(dataQuery)

    const orderedPresets = _.reverse([...presets])

    orderedPresets.forEach((presetsArray, index) => {
        dataQuery = queries[index]
        if (presetsArray.length) {
            resolvedRefArray.values = presetsArray.map(presetDataItem => resolveItemsUnderPreset(presetDataItem, presetItemKey, resolvedRefArray, dataQuery, dataMap))
        }
        const overridesRefArray = dataMap[getOverridesRefArrayId(dataQuery)]
        if (overridesRefArray) {
            const inflatedOverridesRefArrayValues = inflateAndResolveRefArrayValues(overridesRefArray.values, dataMap, dataQuery)
            resolvedRefArray.values = resolvedRefArray.values.concat(inflatedOverridesRefArrayValues)
        }
    })

    return getDataOverrides(resolvedRefArray, dataQuery)
}

// Resolves Style\Layout ref arrays with presets and return data overrides.
// "presets" is an ordered array of presets from the outer refComponent to the inner refComponent
const inflateRefArray = (resolvedRefArray, dataQuery, presets, mapName, dataMap) => {
    if (!presets.flat().length) {
        return inflateWithoutPresets(resolvedRefArray, dataQuery, dataMap)
    }

    return inflateWithPresets(resolvedRefArray, dataQuery, presets, mapName, dataMap)
}

const getPresetsFromRefArray = (presetsRefArray, presetsDataMap) => presetsRefArray.values.map(itemId => {
    const resolvedItem = presetsDataMap[removeHash(itemId)]
    return resolvedItem.type === 'VariantRelation' ?
        {...resolvedItem, to: presetsDataMap[removeHash(resolvedItem.to)]} :
        resolvedItem
})

const getPresets = (presets, presetsDataMap) => presets.map(preset => {
    if (!preset) {
        return []
    }
    if (preset.type === 'RefArray') {
        return getPresetsFromRefArray(preset, presetsDataMap)
    }
    return [preset]
})


module.exports = {
    functionLibrary: {
        inflateRefArray,
        getPresets
    }
}
